import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getSubmissionDossier, respondSubmission } from "@/lib/agencyApi";
import ScoreRing from "@/components/wishire/ScoreRing";
import WishireLogo from "@/components/wishire/WishireLogo";
import { Loader2, Check, X, Calendar, MapPin, Briefcase, Linkedin, CheckCircle2 } from "lucide-react";

const STATUS_LABEL = {
  submitted: "Awaiting your review",
  shortlisted: "Shortlisted by you",
  interview: "Interview requested",
  offer: "Offer stage",
  placed: "Placed",
  rejected: "Passed",
};

export default function SubmissionDossier() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(null);
  const [responded, setResponded] = useState(null);

  useEffect(() => {
    getSubmissionDossier(token).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [token]);

  const respond = async (action) => {
    setBusy(action);
    try {
      const { data } = await respondSubmission(token, action);
      setResponded(data.status);
      setD((x) => ({ ...x, status: data.status }));
    } catch (e) {} finally { setBusy(null); }
  };

  if (err) return <Center>This candidate dossier link is invalid or has expired.</Center>;
  if (!d) return <Center><Loader2 className="animate-spin" size={22} /></Center>;

  const c = d.candidate || {};
  const resolved = ["interview", "shortlisted", "rejected", "placed", "offer"].includes(d.status);

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 md:py-16">
      <div className="container-x max-w-3xl">
        <div className="flex items-center justify-between mb-8">
          <WishireLogo dark size="md" />
          <span className="text-xs font-mono uppercase tracking-widest text-stone-400">Candidate dossier</span>
        </div>

        <p className="text-sm text-stone-500">
          <strong className="text-stone-800">{d.agency_name || "An agency"}</strong> submitted this candidate to{" "}
          <strong className="text-stone-800">{d.client_name}</strong> for:
        </p>
        <h1 className="font-display text-4xl md:text-5xl mt-1 leading-tight">{d.role}</h1>

        {/* Score hero */}
        <div className="mt-8 rounded-[2rem] bg-stone-900 text-stone-50 p-8 md:p-10 relative overflow-hidden wsh-grain">
          <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(500px 320px at 85% 15%, rgba(217,119,6,0.4), transparent 60%)" }} />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8">
            <ScoreRing value={d.wishire_score || 0} size={180} dark />
            <div>
              <p className="font-display text-3xl">{d.candidate_name}</p>
              <p className="text-stone-300 mt-1">{c.headline || c.target_role || "Candidate"}</p>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-stone-400">
                {c.city && <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {c.city}</span>}
                {c.years_experience != null && <span className="inline-flex items-center gap-1.5"><Briefcase size={13} /> {c.years_experience} yrs</span>}
                {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#7db3f0]"><Linkedin size={13} /> LinkedIn</a>}
              </div>
              <p className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-300">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300" /> {STATUS_LABEL[d.status] || d.status}
              </p>
            </div>
          </div>
        </div>

        {/* Score breakdown */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Metric label="Wishire Score" value={`${d.wishire_score ?? 0}/100`} strong />
          <Metric label="Résumé" value={`${d.resume_score ?? 0}/100`} />
          <Metric label="Interview" value={`${d.interview_score ?? 0}/100`} />
        </div>

        {/* Skills */}
        {c.skills?.length > 0 && (
          <div className="mt-6">
            <p className="overline mb-2">Skills</p>
            <div className="flex flex-wrap gap-2">
              {c.skills.map((s) => <span key={s} className="px-3 py-1 rounded-full bg-white border border-stone-200 text-sm text-stone-700">{s}</span>)}
            </div>
          </div>
        )}

        {/* Agency note */}
        {d.note && (
          <div className="mt-6 rounded-2xl bg-[#F5F3ED] border-l-2 border-[#C2410C] p-5">
            <p className="overline mb-1">Why the agency recommends them</p>
            <p className="text-stone-700 leading-relaxed">{d.note}</p>
          </div>
        )}

        {/* Client actions */}
        <div className="mt-10">
          {responded ? (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 flex items-center gap-3" data-testid="dossier-responded">
              <CheckCircle2 size={20} className="text-emerald-700" />
              <p className="text-emerald-900 font-medium">Thanks — we've told {d.agency_name} you {responded === "rejected" ? "passed" : "want to " + responded}.</p>
            </div>
          ) : (
            <>
              <p className="overline mb-3">Your decision</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => respond("interview")} disabled={busy} data-testid="dossier-interview" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-900 text-white font-medium hover:bg-[#C2410C] transition-colors disabled:opacity-60">
                  {busy === "interview" ? <Loader2 size={15} className="animate-spin" /> : <Calendar size={15} />} Request interview
                </button>
                <button onClick={() => respond("shortlist")} disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-stone-300 text-stone-800 font-medium hover:border-stone-900 transition-colors disabled:opacity-60">
                  {busy === "shortlist" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Shortlist
                </button>
                <button onClick={() => respond("reject")} disabled={busy} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-stone-300 text-stone-500 font-medium hover:border-red-400 hover:text-red-600 transition-colors disabled:opacity-60">
                  {busy === "reject" ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} Pass
                </button>
              </div>
            </>
          )}
        </div>

        <p className="mt-10 text-xs text-stone-400 text-center">
          Scored by Wishire — résumé, interview &amp; skills blended into one number. <a href="https://wishire.in" className="underline">wishire.in</a>
        </p>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center text-stone-500 gap-2 px-6 text-center">{children}</div>;
}
function Metric({ label, value, strong }) {
  return (
    <div className={`rounded-2xl p-5 ${strong ? "bg-stone-900 text-stone-50" : "bg-white border border-stone-200"}`}>
      <p className={`font-display text-3xl ${strong ? "text-amber-300" : "text-stone-900"}`}>{value}</p>
      <p className={`overline mt-1 ${strong ? "" : ""}`} style={strong ? { color: "rgba(253,251,247,0.55)" } : {}}>{label}</p>
    </div>
  );
}
